'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var react = require('react');
var compact = _interopDefault(require('lodash/compact'));
var isArray = _interopDefault(require('lodash/isArray'));
var last = _interopDefault(require('lodash/last'));
var debounce = _interopDefault(require('lodash/debounce'));

function useBodyClass(className, condition) {
  react.useEffect(function () {
    var classList = document.body.classList;
    var method = condition ? 'add' : 'remove';
    classList[method](className);
  }, [className, condition]);
}

function useHtmlClass(className, condition) {
  react.useEffect(function () {
    var classList = document.documentElement.classList;
    var method = condition ? 'add' : 'remove';
    classList[method](className);
  }, [className, condition]);
}

function useInterval(callback, delay) {
  var savedCallback = react.useRef(function () {}); // Remember the latest callback.

  react.useEffect(function () {
    savedCallback.current = callback;
  }, [callback]); // Set up the interval.

  react.useEffect(function () {
    function tick() {
      savedCallback.current();
    }

    if (delay !== null) {
      var id = setInterval(tick, delay);
      return function () {
        return clearInterval(id);
      };
    }

    return undefined;
  }, [delay]);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var useMounted = function useMounted() {
  var _useState = react.useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMounted = _useState2[0],
      setIsMounted = _useState2[1];

  react.useEffect(function () {
    return setIsMounted(true);
  }, []);
  return isMounted;
};

function useOnMount(callback) {
  react.useEffect(function () {
    callback();
  }, []);
}

function useOnUnmount(callback) {
  react.useEffect(function () {
    return callback;
  }, []);
}

var usePageId = function usePageId() {
  var location = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.location;
  var home = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'home';
  var id = react.useMemo(function () {
    return compact(location.pathname.split('/')).join('-');
  }, [location.pathname]);
  return id || home;
};

function usePrevious(value) {
  var ref = react.useRef();
  react.useEffect(function () {
    ref.current = value;
  });
  return ref.current;
}

var useProcessOnce = function useProcessOnce(fn, value) {
  var _useState = react.useState(),
      _useState2 = _slicedToArray(_useState, 2),
      result = _useState2[0],
      setResult = _useState2[1];

  react.useEffect(function () {
    if (!result) {
      setResult(fn(value));
    }
  }, []);
  return result || value;
};

function useQueryParam(param) {
  var params = new URLSearchParams(window.location.search);
  return isArray(param) ? param.reduce(function (pv, current) {
    return _objectSpread2({}, pv, _defineProperty({}, current, params.get(current)));
  }, {}) : params.get(param);
}

function useSetState(initialState) {
  var _useReducer = react.useReducer(function (oldState, newState) {
    return _objectSpread2({}, oldState, {}, newState);
  }, initialState),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      state = _useReducer2[0],
      setState = _useReducer2[1];

  return [state, setState];
}

function useSafeState(initialState) {
  var _useSetState = useSetState(initialState),
      _useSetState2 = _slicedToArray(_useSetState, 2),
      state = _useSetState2[0],
      setState = _useSetState2[1];

  var mountedRef = react.useRef(false);
  react.useEffect(function () {
    mountedRef.current = true;
    return function () {
      mountedRef.current = false;
    };
  }, []);

  var safeSetState = function safeSetState(newState) {
    return mountedRef.current && setState(newState);
  };

  return [state, safeSetState];
}

function getElByHash() {
  if (window && window.location.hash && window.location.hash.length > 1) {
    var id = last(window.location.hash.split('#'));
    return document.getElementById(id);
  }

  return null;
}

function scrollToEl(el) {
  var behavior = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'smooth';

  var scroll = function scroll() {
    var top = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    return window.scrollTo({
      top: top,
      behavior: behavior
    });
  };

  if (el) {
    var top = el.offsetTop;
    scroll(top);
  } else {
    scroll(0);
  }
}

var useScrollAfterLoad = function useScrollAfterLoad(doScroll) {
  react.useEffect(function () {
    if (doScroll) {
      var el = getElByHash() || undefined;
      scrollToEl(el);
    }
  }, [doScroll]);
};

function useToggle() {
  var initial = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

  var _useState = react.useState(initial),
      _useState2 = _slicedToArray(_useState, 2),
      value = _useState2[0],
      setValue = _useState2[1];

  var toggler = function toggler() {
    return setValue(!value);
  };

  return [value, toggler];
}

function useWindowDimensions() {
  var delay = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 300;

  var _useState = react.useState({
    height: 0,
    width: 0
  }),
      _useState2 = _slicedToArray(_useState, 2),
      dimensions = _useState2[0],
      setDimensions = _useState2[1];

  react.useEffect(function () {
    var update = function update() {
      return setDimensions({
        height: window.innerHeight,
        width: window.innerWidth
      });
    };

    var handleResize = debounce(update, delay);
    window.addEventListener('resize', handleResize);
    update();
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, [delay]);
  return dimensions;
}

exports.useBodyClass = useBodyClass;
exports.useHtmlClass = useHtmlClass;
exports.useInterval = useInterval;
exports.useMounted = useMounted;
exports.useOnMount = useOnMount;
exports.useOnUnmount = useOnUnmount;
exports.usePageId = usePageId;
exports.usePrevious = usePrevious;
exports.useProcessOnce = useProcessOnce;
exports.useQueryParam = useQueryParam;
exports.useSafeState = useSafeState;
exports.useScrollAfterLoad = useScrollAfterLoad;
exports.useSetState = useSetState;
exports.useToggle = useToggle;
exports.useWindowDimensions = useWindowDimensions;
